import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import { point } from "@turf/helpers";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { getVertexCoordinates } from "fond/draw/modes/base/helper";
import { LayerIds } from "fond/types";

import { getFeatureId, pullInFeatures, refreshSnappedStatus, splitPaths, validPath } from "./helper";

const { constants: Constants } = MapboxDraw;

/**
 * Underground post-drag handler for topology repair.
 *
 * Handles:
 * 1. Point dragged (simple_select): merge duplicate points, remove degenerate paths,
 *    split paths at point location, refresh snapped status.
 * 2. Path vertex dragged (direct_select): check path validity,
 *    refresh snapped status of points at the new vertex location.
 * 3. Whole path dragged (simple_select): remove duplicate same-layer paths,
 *    refresh snapped status of points at endpoints.
 *
 * Designed to be used as an EditingLayer.onAfterDrag callback.
 * Called via `.call(this, draggedFeature, coordPath, connectedIds)`.
 */
export function onAfterDragUnderground(
  this: DrawCustomModeThis,
  draggedFeature: DrawFeature,
  coordPath: string | undefined,
  connectedIds: Set<string | number>
): void {
  if (draggedFeature.type === "Point") {
    handlePointDrag.call(this, draggedFeature as DrawPoint, connectedIds);
  } else if (draggedFeature.type === "LineString" && coordPath !== undefined) {
    handlePathVertexDrag.call(this, draggedFeature as DrawLineString, coordPath);
  } else if (draggedFeature.type === "LineString" && coordPath === undefined) {
    handleWholePathDrag.call(this, draggedFeature as DrawLineString, connectedIds);
  }
}

/**
 * Post-drag topology repair for an underground point that was dragged.
 */
function handlePointDrag(this: DrawCustomModeThis, draggedPoint: DrawPoint, connectedIds: Set<string | number>): void {
  // Find NEW colocated paths/points at the drop location, excluding features
  // that were already connected and moved with the point during drag.
  const { lines, points } = pullInFeatures.call(this, draggedPoint, connectedIds, "mouseup");

  // Reconstruct the connected paths (features that moved with the point) from connectedIds
  const connectedPaths = Array.from(connectedIds)
    .map((id) => this.getFeature(id as string))
    .filter((f): f is DrawLineString => f?.type === "LineString");

  if (points.length > 0) {
    // Point-on-point: merge — delete the pre-existing point(s) so the dragged one (actor) remains selected
    points.forEach((otherPoint) => {
      const pointId = getFeatureId(otherPoint);
      if (pointId) {
        this.deleteFeature(pointId as string, { silent: true });
      }
    });

    connectedPaths.forEach((original: DrawLineString) => {
      // Check for loop: first and last vertex at same location after the merge.
      // e.g. path [A, B, C] where C was dragged to A becomes [A, B, A].
      // Remove the trailing vertex to break the loop → [A, B].
      const coords = original.getCoordinates();
      if (coords.length > 2 && booleanEqual(point(coords[0]), point(coords[coords.length - 1]), { precision: 6 })) {
        original.removeCoordinate(`${coords.length - 1}`);
      }

      // Remove degenerate paths (all vertices at same location)
      if (!validPath(original)) {
        this.deleteFeature(original.id as string, { silent: true });
      }

      // Remove duplicate colocated paths (connected path now matches a path at drop location)
      lines.forEach((line: DrawLineString) => {
        if (booleanEqual(line, original, { precision: 6 })) {
          this.deleteFeature(line.id as string, { silent: true });
        }
      });
    });

    // Refresh snapped status — the point may have absorbed the other point's connections
    refreshSnappedStatus.call(this, draggedPoint);

    // Update snapped status based on whether the point has connections
    const isSnapped = connectedIds.size > 0 || lines.length > 0;
    draggedPoint.setProperty(DRAWING_CONSTANTS.Unsnapped, (!isSnapped).toString());
  } else if (lines.length > 0) {
    // Point-on-path: split each path at the point location.
    // Multiple lines can exist at a T-intersection; we must attempt to split all of them.
    const newPaths = lines.flatMap((line) => splitPaths.call(this, line, draggedPoint));

    // Remove connected paths that are now duplicates of the new split paths
    connectedPaths.forEach((original: DrawLineString) => {
      newPaths.forEach((newPath: DrawLineString) => {
        if (booleanEqual(original, newPath, { precision: 6 })) {
          this.deleteFeature(original.id as string, { silent: true });
        }
      });
    });

    // Refresh snapped status — point now has paths connected after the split
    refreshSnappedStatus.call(this, draggedPoint);
  }
}

/**
 * Post-drag topology repair for a whole underground path that was dragged.
 * Detects and removes duplicate same-layer paths at the drop location,
 * and refreshes snapped status of any underground points at the endpoints.
 */
function handleWholePathDrag(this: DrawCustomModeThis, path: DrawLineString, connectedIds: Set<string | number>): void {
  if (!validPath(path)) {
    this.deleteFeature(path.id as string, { silent: true });
    return;
  }

  const coords = path.getCoordinates();
  const endpoints = [coords[0], coords[coords.length - 1]];
  const seen = new Set<string | number>();

  endpoints.forEach((coord) => {
    const tempPoint = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: {},
      geometry: { type: Constants.geojsonTypes.POINT, coordinates: coord },
    }) as DrawPoint;

    // Exclude features that moved with the drag (already connected)
    const { lines, points } = pullInFeatures.call(this, tempPoint, connectedIds, "mouseup");

    // Remove same-layer paths that are geometrically equal (duplicates)
    lines.forEach((line) => {
      const lineId = getFeatureId(line);
      if (lineId && lineId !== path.id && !seen.has(lineId) && booleanEqual(line, path, { precision: 6 })) {
        seen.add(lineId);
        this.deleteFeature(lineId as string, { silent: true });
      }
    });

    // Refresh snapped status of underground points at this endpoint
    points.forEach((p) => {
      refreshSnappedStatus.call(this, p);
    });
  });
}

/**
 * Post-drag topology repair for an underground path vertex that was dragged.
 * The colocated point (if any) was already moved by the base topological editing.
 * Here we refresh the point's snapped status and check path validity.
 */
function handlePathVertexDrag(this: DrawCustomModeThis, path: DrawLineString, coordPath: string): void {
  const vertexCoords = getVertexCoordinates(path, coordPath);
  if (!vertexCoords) return;

  // Check if the path is now degenerate (all vertices at the same location)
  if (!validPath(path)) {
    this.deleteFeature(path.id as string, { silent: true });
    return;
  }

  // Find underground points at the new vertex location and refresh their snapped status
  const { x, y } = this._ctx.map.project(vertexCoords as mapboxgl.LngLatLike);
  const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [x - 15, y - 15],
    [x + 15, y + 15],
  ];
  const nearbyFeatures = this.featuresAt(null, queryBbox, "click");

  nearbyFeatures.forEach((f) => {
    const featureId = f.properties?.id;
    if (!featureId) return;
    const drawFeature = this.getFeature(featureId);
    if (drawFeature?.type === "Point" && drawFeature.properties?.layerId === LayerIds.inStreetPoints) {
      refreshSnappedStatus.call(this, drawFeature as DrawPoint);
    }
  });
}
